
import Container from '@/components/container';
import Content from '@/components/content';
import Hero from '@/components/hero';
import Header from '@/components/page-specific/commission-calculator/header';
import Buyer from '@/components/page-specific/commission-calculator/buyer';
import Seller from '@/components/page-specific/commission-calculator/seller';
import Footer from '@/components/page-specific/commission-calculator/footer';
import { loadPageData } from '@/utils/prismic';
import headMeta from '@/constants/headMeta';
import routes from '@/constants/routes';
import { StyleSizeType, StyleColorSchemeType, StylePositionType } from '@/ui-kit/styles/enums';
import { StyleFillType } from '@/ui-kit/components/input/enums';
import Button from '@/ui-kit/components/button';
import { ButtonPropsEventType } from '@/ui-kit/components/button/enums';
import Typography from '@/ui-kit/components/typography';
import { TypographyType } from '@/ui-kit/components/typography/enums';
import ProvinceButton from '@/components/page-specific/commission-calculator/province-button';
import { CalculationType } from '@/components/page-specific/commission-calculator/enums';
import { StartPrice } from '@/components/page-specific/buy-sell-your-home/commission-calculator-section/enums';
import { PROVINCES, PROVINCE_OPTIONS } from '@/constants';

const schema = {
  meta: {
    type: 'Object',
    title: {
      type: 'String',
    },
    description: {
      type: 'String',
    },
  },
  hero: {
    type: 'Object',
    required: true,
    image: {
      type: 'Object',
      subtype: 'image',
    },
    title: {
      type: 'String',
    },
    text: {
      type: 'String',
    },
  },
  header: {
    type: 'Object',
    required: true,
    title: {
      type: 'String',
    },
    description: {
      type: 'String',
    },
  },
  seller: {
    type: 'Object',
    required: true,
    slider: {
      type: 'Object',
      title: {
        type: 'Object',
        text: {
          type: 'String',
        },
        tip: {
          type: 'String',
        },
      },
      description: {
        type: 'Object',
        text: {
          type: 'String',
        },
        tip: {
          type: 'String',
        },
      },
    },
    commissions: {
      type: 'Object',
      title: {
        type: 'String',
      },
      reminder: {
        type: 'Object',
        title: {
          type: 'String',
        },
      },
    },
    agent: {
      title: {
        type: 'String',
      },
    },
    result: {
      type: 'Object',
      legend: {
        type: 'Object',
        left: {
          type: 'String',
        },
        right: {
          type: 'String',
        },
      },
      tip: {
        type: 'String',
      },
    },
  },
  buyer: {
    type: 'Object',
    required: true,
    slider: {
      title: {
        type: 'String',
      },
      tip: {
        type: 'String',
      },
    },
    result: {
      type: 'Object',
      legend: {
        type: 'Object',
        left: {
          type: 'String',
        },
        right: {
          type: 'String',
        },
      },
      tip: {
        type: 'String',
      },
    },
  },
  note: {
    type: 'Object',
    required: true,
    ab: {
      type: 'String',
    },
    bc: {
      type: 'String',
    },
    on: {
      type: 'String',
    },
  },
  footer: {
    type: 'Object',
    required: true,
    title: {
      type: 'String',
    },
    description: {
      type: 'String',
    },
    items: {
      type: 'Array',
      text: {
        type: 'String',
      },
    },
  },
  bottom: {
    type: 'Object',
    required: true,
    image: {
      type: 'Object',
      subtype: 'image',
    },
    title: {
      type: 'String',
    },
    seller: {
      type: 'String',
    },
    description: {
      type: 'String',
    },
  },
};

export default {
  name: 'CommissionsCalculatorPage',
  components: {
    Container,
    Content,
    Button,
    ProvinceButton,
    Hero,
    Header,
    Buyer,
    Seller,
    Footer,
    Typography,
  },
  asyncData({ route }) {
    return loadPageData({ schema, $route: route });
  },
  data() {
    return {
      meta: {},
      hero: null,
      header: null,
      seller: null,
      buyer: null,
      footer: null,
      bottom: null,
      tab: CalculationType.Seller,
      activeProvince: PROVINCES.AB,
      buyerPrice: StartPrice.AB,
      StyleSizeType,
      StyleColorSchemeType,
      StylePositionType,
      TypographyType,
      PROVINCES,
      PROVINCE_OPTIONS,
      CalculationType,
      StyleFillType,
      ButtonPropsEventType,
    };
  },
  head() {
    return {
      title: this.meta.title,
      meta: [
        { hid: 'description', name: 'description', content: this.meta.description },
        { property: 'og:title', content: this.meta.title },
        { property: 'og:description', content: this.meta.description },
        { property: 'og:url', content: `${headMeta.domain}${routes.commissionCalculator}` },
        { property: 'og:site_name', content: headMeta.siteName },
        { property: 'og:type', content: headMeta.type },
        { property: 'og:image', content: this.hero?.image?.url },
      ],
    };
  },
  computed: {
    buyerActive() {
      return this.tab === CalculationType.Buyer
        ? StyleColorSchemeType.Secondary
        : StyleColorSchemeType.White;
    },
    sellerActive() {
      return this.tab === CalculationType.Seller
        ? StyleColorSchemeType.Secondary
        : StyleColorSchemeType.White;
    },
  },
  methods: {
    /**
     * On change tab.
     *
     * @track
     * @param {Number} tab
     * @private
     */

    handleUserTypeClick(tab) {
      const tabWithFirstCapitalLetter = tab.charAt(0).toUpperCase() + tab.slice(1);
      this.$track(`Commission calculator_${tabWithFirstCapitalLetter} click`);
      this.tab = tab;
    },

    handleProvinceClick(provinceValue) {
      this.activeProvince = provinceValue;
    },

    /**
     * Update buyer component in case of chainging the province.
     *
     * @param {String} province
     * @param {Number} price
     * @private
     */

    updateValues(province, price) {
      this.activeProvince = province;
      this.buyerPrice = price;
    },
  },
};
